import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { Link, useNavigate } from 'react-router-dom'

const NAV_ITEMS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#features' },
  { label: 'Testimonials', href: '#testimonials' },
]

const FloatingNavbar = () => {
  const navRef = useRef(null)
  const mobileMenuRef = useRef(null)
  const mobileOverlayRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuTl = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (navRef.current) {
      gsap.fromTo(navRef.current, { opacity: 0, y: -16 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', delay: 0.05 })
    }
  }, [])

  useEffect(() => {
    if (!mobileMenuRef.current || !mobileOverlayRef.current) return
    const items = mobileMenuRef.current.querySelectorAll('.mobile-nav-item')
    menuTl.current = gsap.timeline({ paused: true })
    menuTl.current
      .to(mobileOverlayRef.current, { opacity: 1, duration: 0.25, ease: 'power2.out' })
      .fromTo(mobileMenuRef.current, { x: '100%' }, { x: '0%', duration: 0.35, ease: 'power3.out' }, 0)
      .fromTo(items, { opacity: 0, x: 20 }, { opacity: 1, x: 0, duration: 0.25, stagger: 0.05, ease: 'power2.out' }, 0.12)
    return () => { menuTl.current.kill() }
  }, [])

  const toggleMenu = () => {
    if (!menuTl.current) return
    menuOpen ? menuTl.current.reverse() : menuTl.current.play()
    setMenuOpen(!menuOpen)
  }

  return (
    <>
      <nav
        ref={navRef}
        className="fixed left-0 w-full flex items-center justify-between z-[9999] select-none"
        style={{
          height: '60px',
          padding: '0 20px',
          background: 'rgba(210, 184, 154, 0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(169, 140, 107, 0.5)',
          opacity: 0,
        }}
      >
        <div className="flex items-baseline gap-[2px]">
          <span className="font-serif font-light tracking-[0.05em]" style={{ fontSize: 'clamp(0.9rem, 1.1vw, 1.1rem)', color: '#2B2017' }}>Astro</span>
          <span className="font-serif font-light tracking-[0.05em] golden-text" style={{ fontSize: 'clamp(0.9rem, 1.1vw, 1.1rem)' }}>ML</span>
        </div>

        <div className="hidden md:flex items-center" style={{ gap: '36px' }}>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="font-sans font-medium transition-colors duration-300"
              style={{ fontSize: 'clamp(0.75rem, 0.8vw, 0.85rem)', color: '#5A4636' }}
              onMouseEnter={(e) => { e.target.style.color = '#8A6338' }}
              onMouseLeave={(e) => { e.target.style.color = '#5A4636' }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center" style={{ gap: '16px' }}>
          <Link
            to="/login"
            className="font-sans font-medium transition-colors duration-300"
            style={{ fontSize: 'clamp(0.75rem, 0.8vw, 0.85rem)', color: '#5A4636' }}
            onMouseEnter={(e) => { e.target.style.color = '#8A6338' }}
            onMouseLeave={(e) => { e.target.style.color = '#5A4636' }}
          >
            Sign In
          </Link>
          <button
            onClick={() => navigate('/signup')}
            className="font-sans font-semibold transition-all duration-300"
            style={{ fontSize: 'clamp(0.72rem, 0.78vw, 0.82rem)', background: '#8A6338', color: '#F5EDE1', padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', letterSpacing: '0.04em' }}
            onMouseEnter={(e) => { e.target.style.boxShadow = '0 4px 16px rgba(138,99,56,0.25)' }}
            onMouseLeave={(e) => { e.target.style.boxShadow = 'none' }}
          >
            Get Started
          </button>
        </div>

        <button className="md:hidden flex flex-col justify-center items-center gap-[4px] p-2" onClick={toggleMenu} aria-label="Toggle menu">
          <span className="block transition-all duration-300" style={{ width: '16px', height: '1.5px', background: '#2B2017', transform: menuOpen ? 'rotate(45deg) translateY(5.5px)' : 'none' }} />
          <span className="block transition-all duration-300" style={{ width: '16px', height: '1.5px', background: '#2B2017', opacity: menuOpen ? 0 : 1 }} />
          <span className="block transition-all duration-300" style={{ width: '16px', height: '1.5px', background: '#2B2017', transform: menuOpen ? 'rotate(-45deg) translateY(-5.5px)' : 'none' }} />
        </button>
      </nav>

      <div ref={mobileOverlayRef} className="fixed inset-0 z-[9998] md:hidden" style={{ background: 'rgba(0,0,0,0.2)', opacity: 0, pointerEvents: 'none' }} onClick={toggleMenu} />

      <div
        ref={mobileMenuRef}
        className="fixed top-0 right-0 h-full z-[9999] md:hidden flex flex-col"
        style={{ width: 'min(78%, 300px)', background: '#D2B89A', borderLeft: '1px solid #A98C6B', padding: '80px 24px 36px', transform: 'translateX(100%)' }}
      >
        <button className="absolute top-3 right-3 p-2" onClick={toggleMenu} aria-label="Close menu">
          <span className="block" style={{ width: '16px', height: '1.5px', background: '#2B2017', transform: 'rotate(45deg)', position: 'relative' }} />
          <span className="block" style={{ width: '16px', height: '1.5px', background: '#2B2017', transform: 'rotate(-45deg)', position: 'absolute', top: '50%', left: '50%', translate: '-50% -50%' }} />
        </button>

        <div className="flex flex-col gap-6">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="mobile-nav-item font-sans font-medium transition-colors duration-300"
              style={{ fontSize: '0.95rem', color: '#2B2017', opacity: 0 }}
              onClick={toggleMenu}
              onMouseEnter={(e) => { e.target.style.color = '#8A6338' }}
              onMouseLeave={(e) => { e.target.style.color = '#2B2017' }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="mt-8">
          <button
            onClick={() => { toggleMenu(); navigate('/signup'); }}
            className="mobile-nav-item w-full font-sans font-semibold transition-all duration-300"
            style={{ fontSize: '0.82rem', background: '#8A6338', color: '#F5EDE1', padding: '11px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', opacity: 0, letterSpacing: '0.04em' }}
          >
            Get Started
          </button>
        </div>
      </div>
    </>
  )
}

export default FloatingNavbar
